import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Divider,
  IconButton,
  MenuList,
  Popover,
  Typography,
} from "@mui/material";
import { UserAddOutlined, UserDeleteOutlined } from "@ant-design/icons";
import { salesLogoutUser } from "../../../store/actions/salesActions";
import { useNavigate } from "react-router-dom";
import { useSalesAuth } from "../../../hooks/useSalesAuth";
import axios from "axios";

export const SalesAccountPopover = (props) => {
  const { user } = useSalesAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { anchorEl, onClose, open } = props;
  const fileInputRef = useRef(null);
  const [signOutLoading, setSignOutLoading] = useState(false);

  const removeAvatar = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/remove-avatar`,
        {},
        {
          withCredentials: true,
        }
      );

      props?.setAvatarUrl("");
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch (e) {
      console.error("Error removing avatar:", e);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert("File is too large. Please select a file smaller than 5MB.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/upload-avatar`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      props?.setAvatarUrl(resp?.data.data);
    } catch (e) {
      console.error("Error uploading avatar:", e);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const exitHandler = async () => {
    setSignOutLoading(true);
    try {
      await dispatch(salesLogoutUser());
      navigate("/sales/login");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setSignOutLoading(false);
    }
  };

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: "left",
        vertical: "bottom",
      }}
      onClose={onClose}
      open={open}
      sx={{ width: "auto" }}
    >
      <Box
        sx={{
          py: 1.5,
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" align="center" sx={{ marginTop: "1rem" }}>
          Sales User
        </Typography>
        <div
          style={{
            borderBottom: "3px solid #d3d3d3",
            marginTop: "3px",
          }}
        ></div>
        <Typography variant="overline" align="center">
          {user?.full_name || "Sales User"}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {user?.email || ""}
        </Typography>
        <Divider />
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleAvatarChange}
        style={{ display: "none" }}
        accept="image/*"
      />
      <Typography align="center" variant="body2">
        Avatar
      </Typography>
      <Box
        sx={{
          borderTop: "1px solid #d3d3d3",
          pb: 1,
        }}
      >
        {props?.avatarUrl ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                px: 2,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <IconButton
                  aria-label="remove"
                  sx={{ color: "#f44336" }}
                  onClick={removeAvatar}
                >
                  <UserDeleteOutlined />
                </IconButton>
                <Typography variant="body2" sx={{ fontSize: "12px" }}>
                  Remove
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <IconButton
                  aria-label="change"
                  sx={{ color: "#1976d2" }}
                  onClick={triggerFileInput}
                >
                  <UserAddOutlined />
                </IconButton>
                <Typography variant="body2" sx={{ fontSize: "12px" }}>
                  Change
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ textAlign: "center", px: 2 }}>
              <IconButton
                aria-label="add"
                sx={{ color: "#4caf50" }}
                onClick={triggerFileInput}
              >
                <UserAddOutlined />
              </IconButton>
              <Typography variant="body2" sx={{ fontSize: "12px" }}>
                Add
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <MenuList
        disablePadding
        dense
        sx={{
          "& > *": {
            borderRadius: 1,
          },
        }}
      >
        <LoadingButton
          loading={signOutLoading}
          variant="contained"
          fullWidth
          size="small"
          onClick={exitHandler}
          sx={{ m: 1 }}
        >
          Sign out
        </LoadingButton>
      </MenuList>
    </Popover>
  );
};
